import { MessageCircle, ArrowDown, ShieldCheck, Sparkles } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";

const WA = "https://wa.me/56936400479?text=Hola%2C%20quiero%20cotizar%20una%20base%20de%20datos";

export default function Hero() {
  return (
    <section
      id="inicio"
      data-testid={TESTIDS.heroSection}
      className="relative min-h-screen pt-32 pb-20 overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg"
          alt="Fibra óptica futurista"
          className="w-full h-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#040812] via-[#040812]/85 to-[#040812]" />
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute inset-0 radial-glow" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* LEFT */}
          <div className="lg:col-span-7">
            {/* Overline */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(0,240,255,0.3)] bg-[rgba(0,240,255,0.06)] mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f0ff] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00f0ff]"></span>
              </span>
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00f0ff]">
                Bases B2B · Chile · 2026
              </span>
            </div>

            {/* H1 */}
            <h1 className="font-display font-black text-white text-[44px] sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
              Bases de datos
              <br />
              <span className="text-[#00f0ff] text-cyan-glow">B2B</span> que
              <br />
              <span className="italic font-light">de verdad</span> convierten.
            </h1>

            <p className="text-[17px] sm:text-lg text-[#94a3b8] leading-relaxed max-w-xl mb-10">
              Contactos verificados de empresas en Chile, segmentados por rubro,
              región y tamaño. Listos para usar — y en{" "}
              <span className="text-white font-semibold">
                cumplimiento con la nueva Ley 21.719
              </span>{" "}
              de Protección de Datos.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href={WA}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={TESTIDS.heroPrimaryCta}
                className="group inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1ebe5a] text-white font-semibold text-base px-7 py-4 rounded-full transition-all shadow-[0_12px_40px_-8px_rgba(37,211,102,0.5)] hover:shadow-[0_20px_50px_-8px_rgba(37,211,102,0.7)] hover:-translate-y-0.5"
              >
                <MessageCircle size={20} />
                <span>Cotizar por WhatsApp</span>
                <span className="font-mono text-xs opacity-80">
                  +56 9 3640 0479
                </span>
              </a>
              <a
                href="#servicios"
                data-testid={TESTIDS.heroSecondaryCta}
                className="inline-flex items-center justify-center gap-2 border border-[rgba(0,240,255,0.4)] hover:border-[#00f0ff] text-white font-medium text-base px-7 py-4 rounded-full transition-all hover:bg-[rgba(0,240,255,0.06)]"
              >
                <span>Ver servicios</span>
                <ArrowDown size={16} />
              </a>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-xl pt-8 border-t border-[rgba(0,240,255,0.12)]">
              <div>
                <div className="font-display font-black text-2xl sm:text-3xl text-white">
                  &lt;24h
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#94a3b8] mt-1">
                  Entrega bases<br/>estándar
                </div>
              </div>
              <div>
                <div className="font-display font-black text-2xl sm:text-3xl text-[#00f0ff]">
                  100%
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#94a3b8] mt-1">
                  Fuentes públicas<br/>legales
                </div>
              </div>
              <div>
                <div className="font-display font-black text-2xl sm:text-3xl text-white">
                  Ley 21.719
                </div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-[#94a3b8] mt-1">
                  En cumplimiento<br/>normativo
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Floating compliance card */}
          <div className="lg:col-span-5 relative">
            <div className="relative animate-float">
              {/* Glow */}
              <div className="absolute -inset-6 bg-[#00f0ff]/10 rounded-3xl blur-3xl" />

              <div
                data-testid={TESTIDS.complianceBadge}
                className="gradient-border relative overflow-hidden p-8"
              >
                <div className="absolute inset-0 compliance-shimmer pointer-events-none" />

                <div className="relative">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[rgba(0,240,255,0.1)] border border-[rgba(0,240,255,0.3)] flex items-center justify-center">
                      <ShieldCheck className="text-[#00f0ff]" size={22} />
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00f0ff]">
                        Sello de Cumplimiento
                      </div>
                      <div className="font-display font-bold text-white text-lg leading-tight">
                        Ley 21.719
                      </div>
                    </div>
                  </div>

                  <p className="text-[14px] text-[#cbd5e1] leading-relaxed mb-6">
                    Todos nuestros datos provienen de{" "}
                    <span className="text-white font-semibold">
                      registros públicos oficiales
                    </span>{" "}
                    y fuentes legalmente permitidas, conforme a la nueva Ley de
                    Protección de Datos Personales de Chile (vigente desde
                    diciembre 2026).
                  </p>

                  <div className="space-y-2.5">
                    {[
                      "Fuentes públicas verificadas",
                      "Bases trazables y documentadas",
                      "Sólo datos de carácter empresarial",
                      "Sin información sensible o privada",
                    ].map((t) => (
                      <div key={t} className="flex items-start gap-2.5">
                        <div className="mt-1 w-1.5 h-1.5 rounded-full bg-[#00f0ff] shadow-[0_0_8px_rgba(0,240,255,0.8)]" />
                        <span className="text-[13px] text-[#94a3b8]">{t}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-5 border-t border-[rgba(0,240,255,0.12)] flex items-center gap-2 text-[#00f0ff]">
                    <Sparkles size={14} />
                    <span className="font-mono text-[11px] uppercase tracking-wider">
                      Compra con tranquilidad
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
